import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { motion } from "framer-motion";
import InteractiveDemo from "@/components/InteractiveDemo";
import { track, captureReferral } from "@/lib/track";
import {
  Camera, Sparkles, FileSpreadsheet, ShieldCheck, Lock, EyeOff, Trash2,
  Mail, LayoutGrid, ArrowRight, Check, Menu, X, Zap, Clock,
} from "lucide-react";

const GOLD = "#D4C3A3";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`max-w-7xl mx-auto px-6 lg:px-8 ${className}`}>{children}</section>
  );
}

function Eyebrow({ children }) {
  return (
    <span className="inline-block text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4C3A3] mb-4">{children}</span>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    captureReferral();
    track("landing_view", { path: window.location.pathname });
  }, []);

  const goSignup = () => navigate("/login?mode=signup");
  const goLogin = () => navigate("/login");

  if (loading || user) return <div className="min-h-screen bg-[#050505]" />;

  return (
    <div className="noise-overlay min-h-screen bg-[#050505] text-zinc-100 overflow-x-hidden antialiased font-sans">
      {/* NAV */}
      <nav data-testid="landing-nav" className="fixed top-0 w-full z-50 bg-[#050505]/70 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/brand-icon-gold.png" alt="Receipt Republic" className="h-8 w-8 object-contain rounded-lg" />
            <span className="font-display font-extrabold text-lg tracking-tight text-white">Receipt Republic</span>
          </button>
          <div className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">{l.label}</a>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-4">
            <button onClick={goLogin} data-testid="nav-login" className="text-sm font-semibold text-zinc-300 hover:text-white transition-colors px-2">Log in</button>
            <Button onClick={goSignup} data-testid="nav-signup" className="rounded-full px-5 bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] hover:scale-[1.03] transition-all duration-300 font-semibold">Start free trial</Button>
          </div>
          <button className="md:hidden p-2 text-zinc-200" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-menu-toggle">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/[0.06] bg-[#050505] px-6 py-4 space-y-3">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="block text-sm font-medium text-zinc-300">{l.label}</a>
            ))}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={goLogin} className="flex-1 rounded-full border-white/15 bg-transparent text-white hover:bg-white/5">Log in</Button>
              <Button onClick={goSignup} className="flex-1 rounded-full bg-[#D4C3A3] text-black hover:bg-[#E5D6B8]">Start free trial</Button>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <header className="relative pt-28 pb-24 lg:pt-40 lg:pb-28 overflow-hidden">
        {/* ambient glows */}
        <div className="absolute top-[-6rem] left-1/2 -translate-x-1/2 h-[42rem] w-[80rem] max-w-full bg-[radial-gradient(ellipse_at_top,rgba(212,195,163,0.14),transparent_60%)] pointer-events-none" />
        <div className="absolute -top-10 right-[-10rem] h-96 w-96 rounded-full bg-[#D4C3A3]/[0.06] blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,rgba(5,5,5,0.4))] pointer-events-none" />
        <Section className="relative grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold px-3.5 py-1.5 rounded-full bg-white/[0.04] text-[#D4C3A3] uppercase tracking-[0.2em] mb-7 border border-white/10">
              <Zap className="h-3.5 w-3.5" /> Built for gig workers
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[4.6rem] tracking-tighter font-extrabold leading-[0.95] mb-6 text-white">
              Never miss a<br />
              <span className="text-[#D4C3A3]">tax write-off</span> again.
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed mb-9 max-w-md">
              Snap a receipt and our AI files it under the right IRS Schedule C category — so gig workers keep more of every dollar. No spreadsheets, no shoebox, no missed deductions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={goSignup} data-testid="hero-signup" className="h-13 rounded-full px-8 py-6 text-base bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] font-semibold shadow-[0_10px_40px_-10px_rgba(212,195,163,0.5)] hover:shadow-[0_18px_50px_-10px_rgba(212,195,163,0.6)] hover:-translate-y-0.5 transition-all duration-300">
                Start 7-day free trial <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })} variant="outline" data-testid="hero-how" className="h-13 rounded-full px-8 py-6 text-base border-white/15 bg-transparent text-white font-semibold hover:bg-white/[0.04] hover:text-white">
                See how it works
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-9 text-sm text-zinc-500 font-medium">
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#D4C3A3]" /> IRS Schedule C ready</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#D4C3A3]" /> No bank login needed</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#D4C3A3]" /> 20 free receipts / month</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }} className="lg:col-span-7">
            <div className="relative">
              <div className="absolute -inset-6 bg-[radial-gradient(ellipse_at_center,rgba(212,195,163,0.18),transparent_65%)] blur-2xl pointer-events-none" />
              <div className="relative rounded-[1.75rem] bg-white/[0.03] backdrop-blur-xl border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] p-3 sm:p-4">
                <InteractiveDemo onStart={goSignup} />
              </div>
            </div>
          </motion.div>
        </Section>
      </header>

      {/* TRUST BAND */}
      <Section className="py-4">
        <div data-testid="trust-band" className="flex flex-wrap justify-center md:justify-between items-center gap-6 md:gap-8 py-7 px-4 md:px-10 border border-white/[0.06] bg-white/[0.02] rounded-3xl">
          {[
            { icon: ShieldCheck, text: "IRS Schedule C Ready" },
            { icon: Lock, text: "Bank-Level Security" },
            { icon: EyeOff, text: "No Bank Login Required" },
            { icon: Trash2, text: "Receipt Images Never Stored" },
          ].map((t) => (
            <div key={t.text} className="flex items-center gap-2.5 text-zinc-300">
              <t.icon className="h-5 w-5 text-[#D4C3A3] shrink-0" />
              <span className="text-sm font-semibold whitespace-nowrap">{t.text}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how" className="py-24 lg:py-32">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-2xl mb-16">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold text-white">Tax-ready in three taps.</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            { icon: Camera, title: "Snap or forward", desc: "Take a photo — or forward an email receipt to your private inbox address. We grab it instantly." },
            { icon: Sparkles, title: "AI categorizes", desc: "Our AI reads the vendor, amount and date, then assigns the correct Schedule C category. You just confirm." },
            { icon: FileSpreadsheet, title: "Export at tax time", desc: "Download one clean file of every deduction — ready for your CPA, TurboTax or Schedule C." },
          ].map((s, i) => (
            <motion.div key={s.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="relative p-8 bg-white/[0.02] border border-white/10 rounded-3xl hover:border-white/20 hover:-translate-y-1 transition-all duration-300">
              <span className="absolute top-8 right-8 font-display text-5xl font-extrabold text-white/[0.05]">{i + 1}</span>
              <div className="h-12 w-12 rounded-2xl bg-[#D4C3A3]/10 border border-[#D4C3A3]/20 flex items-center justify-center mb-6">
                <s.icon className="h-6 w-6 text-[#D4C3A3]" />
              </div>
              <h3 className="font-display text-xl font-bold mb-2 text-white">{s.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FEATURES BENTO */}
      <Section id="features" className="py-24 lg:py-32">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
          <Eyebrow>Everything you need</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold text-white">Less admin. More miles.</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <motion.div custom={0} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="md:col-span-7 bg-white/[0.02] border border-white/10 p-10 rounded-3xl flex flex-col justify-between min-h-[300px] hover:border-white/20 transition-colors">
            <div>
              <div className="h-12 w-12 rounded-2xl bg-[#D4C3A3]/10 border border-[#D4C3A3]/20 flex items-center justify-center mb-6"><Sparkles className="h-6 w-6 text-[#D4C3A3]" /></div>
              <h3 className="font-display text-2xl font-bold mb-3 text-white">AI extraction engine</h3>
              <p className="text-zinc-400 leading-relaxed max-w-md">Blurry gas receipt at 2am? No problem. Our vision AI pulls the vendor, total and date automatically — even from crumpled paper and email confirmations.</p>
            </div>
            <div className="flex gap-2 mt-7 flex-wrap">
              {["Car & truck", "Meals", "Supplies", "Utilities"].map((c) => (
                <span key={c} className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-zinc-300">{c}</span>
              ))}
            </div>
          </motion.div>
          <motion.div custom={1} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="md:col-span-5 bg-[#D4C3A3] text-black p-8 rounded-3xl flex flex-col justify-between min-h-[300px] relative overflow-hidden">
            <div className="absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-black/5" />
            <div className="h-12 w-12 rounded-2xl bg-black/10 flex items-center justify-center mb-5 relative"><Mail className="h-6 w-6" /></div>
            <div className="relative">
              <h3 className="font-display text-2xl font-bold mb-2">Forward-by-email inbox</h3>
              <p className="text-black/70 leading-relaxed font-medium">Get a private address. Forward any email receipt and it's auto-imported and categorized — hands-free.</p>
            </div>
          </motion.div>
          <motion.div custom={2} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="md:col-span-4 bg-white/[0.02] border border-white/10 p-8 rounded-3xl hover:border-white/20 transition-colors">
            <div className="h-12 w-12 rounded-2xl bg-[#D4C3A3]/10 border border-[#D4C3A3]/20 flex items-center justify-center mb-5"><LayoutGrid className="h-6 w-6 text-[#D4C3A3]" /></div>
            <h3 className="font-display text-xl font-bold mb-2 text-white">15 Schedule C categories</h3>
            <p className="text-zinc-400 leading-relaxed">Every deduction mapped to the exact IRS line — editable in one tap.</p>
          </motion.div>
          <motion.div custom={3} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="md:col-span-4 bg-white/[0.02] border border-white/10 p-8 rounded-3xl hover:border-white/20 transition-colors">
            <div className="h-12 w-12 rounded-2xl bg-[#D4C3A3]/10 border border-[#D4C3A3]/20 flex items-center justify-center mb-5"><Clock className="h-6 w-6 text-[#D4C3A3]" /></div>
            <h3 className="font-display text-xl font-bold mb-2 text-white">Month & YTD dashboard</h3>
            <p className="text-zinc-400 leading-relaxed">Watch your write-offs add up in real time, all year long.</p>
          </motion.div>
          <motion.div custom={4} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="md:col-span-4 bg-white/[0.02] border border-white/10 p-8 rounded-3xl hover:border-white/20 transition-colors">
            <div className="h-12 w-12 rounded-2xl bg-[#D4C3A3]/10 border border-[#D4C3A3]/20 flex items-center justify-center mb-5"><FileSpreadsheet className="h-6 w-6 text-[#D4C3A3]" /></div>
            <h3 className="font-display text-xl font-bold mb-2 text-white">CSV, Excel & PDF export</h3>
            <p className="text-zinc-400 leading-relaxed">Tax time takes seconds. Hand a clean file to your CPA and you're done.</p>
          </motion.div>
        </div>
      </Section>

      {/* POSITIONING */}
      <Section className="py-12">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          className="bg-[#0A0A0A] text-white rounded-[2.5rem] border border-white/[0.06] p-12 md:p-20 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#D4C3A3]/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#D4C3A3]/[0.06] blur-3xl" />
          <p className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#D4C3A3] mb-5 relative">Why switch</p>
          <h2 className="font-display text-4xl sm:text-6xl tracking-tighter font-extrabold leading-[0.98] max-w-4xl mx-auto relative">
            Expensify and Dext were built for accountants.<br />
            <span className="text-[#D4C3A3]">This one's built for you.</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto mt-6 relative">
            No enterprise clutter. No 40-field forms. Just snap, confirm, export — the fastest way for a side-hustler to stay tax-ready.
          </p>
          <Button onClick={goSignup} data-testid="positioning-cta" className="mt-10 rounded-full px-8 py-6 text-base bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] hover:scale-[1.03] transition-all duration-300 font-semibold relative">
            Try it free for 7 days <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </motion.div>
      </Section>

      {/* WHY I BUILT THIS — founder note */}
      <Section className="py-16 lg:py-20">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          className="max-w-3xl mx-auto rounded-[2rem] border border-white/10 bg-white/[0.02] p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-20 -left-16 h-56 w-56 rounded-full bg-[#D4C3A3]/[0.07] blur-3xl pointer-events-none" />
          <Eyebrow>Why I built this</Eyebrow>
          <div className="relative space-y-4 text-zinc-300 leading-relaxed text-lg">
            <p>
              I drove rideshare and did freelance gigs on the side, and every April the same thing happened:
              a shoebox of faded receipts, a panicked weekend, and the sinking feeling that I'd left real money
              on the table in write-offs I couldn't prove.
            </p>
            <p>
              The "expense apps" out there were built for accountants and big companies — 40-field forms, bank
              logins, monthly fees that cost more than they saved me. So I built the tool I actually wanted:
              <span className="text-white font-semibold"> snap a receipt, let AI file it under the right IRS Schedule C line, export a clean file at tax time.</span> That's it.
            </p>
            <p className="text-zinc-400">
              It's early, it's honest, and it's priced like a coffee. If it saves you one missed deduction, it's paid for itself.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-8">
            <img src="/brand-icon-gold.png" alt="" className="h-11 w-11 object-contain rounded-xl border border-white/10" />
            <div>
              <p className="font-display font-bold text-white leading-tight">The Receipt Republic founder</p>
              <p className="text-sm text-zinc-500">Built for gig workers, by one</p>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* PRICING */}
      <Section id="pricing" className="py-24 lg:py-32">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-center max-w-2xl mx-auto mb-16">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold text-white">One plan. Priced like a coffee.</h2>
          <p className="text-zinc-400 mt-4 text-lg">Start free. Upgrade only when you're ready to go unlimited.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto items-center">
          <div data-testid="plan-free" className="p-10 rounded-3xl border border-white/10 bg-white/[0.02] flex flex-col h-full">
            <h3 className="font-display text-xl font-bold text-white">Free</h3>
            <div className="flex items-end gap-1 my-4">
              <span className="font-display text-5xl font-extrabold tracking-tight text-white font-mono-num">$0</span>
              <span className="text-zinc-500 mb-2">/ month</span>
            </div>
            <p className="text-zinc-400 mb-6">Perfect for testing the waters.</p>
            <ul className="space-y-3 mb-8 flex-1">
              {["20 receipts / month", "AI categorization", "Month & YTD dashboard", "CSV export"].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-zinc-300"><Check className="h-5 w-5 text-[#D4C3A3] shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button onClick={goSignup} variant="outline" data-testid="free-cta" className="w-full h-12 rounded-full border-white/15 bg-transparent text-white font-semibold hover:bg-white/[0.04] hover:text-white">Get started free</Button>
          </div>
          <div data-testid="plan-pro" className="p-12 rounded-3xl border border-[#D4C3A3]/40 bg-[#0A0A0A] shadow-[0_0_60px_-15px_rgba(212,195,163,0.25)] relative flex flex-col md:scale-105 z-10 overflow-hidden">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#D4C3A3]/10 blur-3xl" />
            <span className="absolute top-6 right-8 bg-[#D4C3A3] text-black text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-[0.15em]">Most popular</span>
            <h3 className="font-display text-xl font-bold text-[#D4C3A3] relative">Pro</h3>
            <div className="flex items-end gap-1 my-4 relative">
              <span className="font-display text-5xl font-extrabold tracking-tight text-white font-mono-num">$6</span>
              <span className="text-zinc-500 mb-2">/ month</span>
            </div>
            <p className="text-zinc-400 mb-6 relative">Unlimited everything. 7-day free trial.</p>
            <ul className="space-y-3 mb-8 flex-1 relative">
              {["Unlimited receipt scans", "Forward-by-email inbox", "Priority AI processing", "Exclusive Dark Pro theme", "Cancel anytime"].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-zinc-200"><Check className="h-5 w-5 text-[#D4C3A3] shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button onClick={goSignup} data-testid="pro-cta" className="w-full h-12 rounded-full bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] hover:scale-[1.02] transition-all duration-300 font-semibold relative">Start 7-day free trial</Button>
          </div>
        </div>
      </Section>

      {/* VALUE / STATS (honest) */}
      <Section className="py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { stat: "15", label: "IRS Schedule C categories mapped automatically" },
            { stat: "$0", label: "to start — 20 free receipts every month" },
            { stat: "< 5s", label: "average time to capture & categorize a receipt" },
          ].map((c) => (
            <motion.div key={c.label} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="p-8 bg-white/[0.02] border border-white/10 rounded-3xl text-center">
              <p className="font-display text-4xl font-extrabold text-[#D4C3A3] mb-2 font-mono-num">{c.stat}</p>
              <p className="text-zinc-400 text-sm leading-relaxed">{c.label}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="py-24 lg:py-32 max-w-3xl">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold text-white">Questions, answered.</h2>
        </motion.div>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {[
            { q: "Do I need to connect my bank account?", a: "No. Receipt Republic never asks for bank logins. You simply snap or forward receipts — nothing sensitive is linked." },
            { q: "Is this really okay for my taxes?", a: "Yes. Every expense is mapped to a standard IRS Schedule C category and exported as a clean file your CPA or TurboTax can use directly." },
            { q: "What happens to my receipt photos?", a: "Images are processed to extract the details and are not stored publicly. Only the extracted data (vendor, amount, date, category) is kept in your account." },
            { q: "What does the free plan include?", a: "20 receipts per month with full AI categorization, dashboard and CSV export. Upgrade to Pro ($6/mo) for unlimited receipts and email forwarding." },
            { q: "Can I cancel anytime?", a: "Absolutely. Cancel before your 7-day trial ends and you won't be charged. After that it's month-to-month with no lock-in." },
          ].map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger className="text-left font-semibold text-base text-white hover:text-[#D4C3A3] hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-400 leading-relaxed text-base">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* FINAL CTA + FOOTER */}
      <footer className="border-t border-white/[0.06] pt-20 pb-12">
        <Section>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="bg-[#0A0A0A] border border-white/[0.06] rounded-[2.5rem] p-12 md:p-16 text-center mb-16 relative overflow-hidden">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-[40rem] max-w-full bg-[radial-gradient(ellipse_at_top,rgba(212,195,163,0.14),transparent_60%)] pointer-events-none" />
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-extrabold leading-tight mb-4 text-white relative">Stop dreading tax season.</h2>
            <p className="text-zinc-400 text-lg max-w-lg mx-auto mb-8 relative">Join gig workers who turned their shoebox of receipts into a tidy, tax-ready export. Free to start.</p>
            <Button onClick={goSignup} data-testid="footer-cta" className="rounded-full px-8 py-6 text-base bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] hover:scale-[1.03] transition-all duration-300 font-semibold relative">
              Start your 7-day free trial <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </motion.div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <img src="/brand-icon-gold.png" alt="Receipt Republic" className="h-8 w-8 object-contain rounded-lg" />
              <span className="font-display font-extrabold tracking-tight text-white">Receipt Republic</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
              <button onClick={goLogin} className="hover:text-white transition-colors">Log in</button>
            </div>
          </div>
          <p className="text-center md:text-left text-xs text-zinc-600 mt-8">© {new Date().getFullYear()} Receipt Republic. Built for gig workers filing Schedule C. Not tax advice.</p>
        </Section>
      </footer>
    </div>
  );
}
